import React from "react";
import { WE_CRM_ACCESS } from "../../../config/api";
import {
  LayoutGridIcon,
  Invoice01Icon,
  Calculator01Icon,
  NoteEditIcon,
  Contact01Icon,
  ChartHistogramIcon,
  ChampionIcon,
  Calendar01Icon,
  FileValidationIcon,
  UserMultiple02Icon,
  FilterIcon,
  UserAdd01Icon,
  Analytics01Icon,
  BankIcon,
  Invoice02Icon,
  Layout01Icon,
  Settings01Icon,
  HelpCircleIcon,
  SmileIcon,
  Call02Icon,
  StarIcon,
  Time02Icon,
  Folder01Icon,
  Building02Icon,
  Agreement01Icon,
  LegalDocument01Icon,
  Presentation01Icon,
  CreditCardIcon,
  Ticket01Icon,
  PackageDelivered01Icon,
  Building01Icon,
  User02Icon,
} from "hugeicons-react";

const ICON_SIZE = 15;
const ICON_STROKE_WIDTH = 1.6;

const renderIcon = (IconComponent) => (
  <IconComponent size={ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />
);

/**
 * Navigation Item Definitions for Super Admin Role
 */
export const SUPER_ADMIN_MENU_CONFIG = [
  {
    key: "overview",
    label: "Platform Health & Metrics",
    icon: renderIcon(Analytics01Icon),
    featureName: "Platform Health & Metrics",
  },
  {
    key: "requests",
    label: "Signups & Plan Requests",
    icon: renderIcon(Time02Icon),
    featureName: "Signups & Plan Requests",
  },
  {
    key: "companies",
    label: "Tenant Organizations",
    icon: renderIcon(Building01Icon),
    featureName: "Tenant Organizations",
  },
  {
    key: "payments",
    label: "Subscription Billing",
    icon: renderIcon(CreditCardIcon),
    featureName: "Subscription Billing",
  },
  {
    key: "rm",
    label: "Account Managers",
    icon: renderIcon(User02Icon),
    featureName: "Account Managers",
  },
];

/**
 * Raw Navigation Item Definitions for Admin Role
 */
export const ADMIN_MENU_CONFIG = [
  {
    key: "overview",
    label: "Dashboard",
    icon: renderIcon(LayoutGridIcon),
    featureName: "Dashboard",
  },
  // When isCrmAdmin is true, CRM folder structure is included
  {
    key: "crm_folder",
    label: "CRM",
    icon: renderIcon(Folder01Icon),
    isFolder: true,
    crmOnly: true,
    children: [
      {
        type: "group",
        label: "Client Views",
        children: [
          {
            key: "crm_clients",
            label: "Enterprise Accounts",
            icon: renderIcon(Building02Icon),
            featureName: "Enterprise Accounts",
          },
        ],
      },
      {
        type: "group",
        label: "Documents",
        children: [
          {
            key: "crm_sla",
            label: "Service Agreements (SLA)",
            icon: renderIcon(Agreement01Icon),
            featureName: "Service Agreements (SLA)",
          },
          {
            key: "crm_nda",
            label: "Legal & NDAs",
            icon: renderIcon(LegalDocument01Icon),
            featureName: "Legal & NDAs",
          },
        ],
      },
      {
        type: "group",
        label: "Operations",
        children: [
          {
            key: "crm_amc",
            label: "Maintenance Contracts (AMC)",
            icon: renderIcon(Presentation01Icon),
            featureName: "Maintenance Contracts (AMC)",
          },
          {
            key: "crm_payments",
            label: "Milestone Payments",
            icon: renderIcon(CreditCardIcon),
            featureName: "Milestone Payments",
          },
          {
            key: "crm_tickets",
            label: "Support Helpdesk",
            icon: renderIcon(Ticket01Icon),
            featureName: "Support Helpdesk",
          },
        ],
      },
      {
        type: "group",
        label: "Delivery",
        children: [
          {
            key: "crm_projects",
            label: "Client Deliverables",
            icon: renderIcon(PackageDelivered01Icon),
            featureName: "Client Deliverables",
          },
        ],
      },
    ],
  },
  // Sales / Standard Admin Groups
  {
    type: "group",
    label: "Shortcuts",
    children: [
      {
        key: "invoice",
        label: "Invoices",
        icon: renderIcon(Invoice01Icon),
        featureName: "Invoices",
      },
      {
        key: "quotation",
        label: "Quotations",
        icon: renderIcon(Calculator01Icon),
        featureName: "Quotations",
      },
      {
        key: "proposals",
        label: "Proposals",
        icon: renderIcon(NoteEditIcon),
        featureName: "Proposals",
      },
    ],
  },
  {
    type: "group",
    label: "Admin Views",
    children: [
      {
        key: "leads",
        label: "Leads",
        icon: renderIcon(Contact01Icon),
        featureName: "Leads",
      },
      {
        key: "pipeline",
        label: "Pipeline",
        icon: renderIcon(ChartHistogramIcon),
        featureName: "Pipeline",
      },
      {
        key: "converted",
        label: "Converted",
        icon: renderIcon(ChampionIcon),
        featureName: "Converted",
      },
      {
        key: "activities",
        label: "Schedule",
        icon: renderIcon(Calendar01Icon),
        featureName: "Schedule",
      },
      {
        key: "crm_client_onboard_requests",
        label: "Onboarding",
        icon: renderIcon(FileValidationIcon),
        featureName: "Onboarding",
        weCrmOnly: true,
      },
      {
        key: "our_clients",
        label: "Clients",
        icon: renderIcon(UserMultiple02Icon),
        featureName: "Clients",
      },
      {
        key: "remarks_filter",
        label: "Notes Search",
        icon: renderIcon(FilterIcon),
        featureName: "Notes Search",
      },
      {
        key: "employees",
        label: "Employee Management",
        icon: renderIcon(UserAdd01Icon),
        featureName: "Employee Management",
      },
      {
        key: "reports",
        label: "Reports",
        icon: renderIcon(Analytics01Icon),
        featureName: "Reports",
      },
    ],
  },
  {
    type: "group",
    label: "Organization",
    children: [
      {
        key: "company",
        label: "Branding",
        icon: renderIcon(BankIcon),
        featureName: "Branding",
      },
      {
        key: "invoice_settings",
        label: "Billing Settings",
        icon: renderIcon(Invoice02Icon),
        featureName: "Billing Settings",
      },
      {
        key: "proposal_settings",
        label: "Proposal Templates",
        icon: renderIcon(Layout01Icon),
        featureName: "Proposal Templates",
      },
      {
        key: "settings",
        label: "System Settings",
        icon: renderIcon(Settings01Icon),
        featureName: "System Settings",
      },
      {
        key: "support",
        label: "Help",
        icon: renderIcon(HelpCircleIcon),
        featureName: "Help",
      },
    ],
  },
];

/**
 * Raw Navigation Item Definitions for Employee Role
 */
export const EMPLOYEE_MENU_CONFIG = [
  {
    key: "overview",
    label: "My Workspace",
    icon: renderIcon(LayoutGridIcon),
    featureName: "My Workspace",
  },
  {
    type: "group",
    label: "Shortcuts",
    children: [
      {
        key: "invoices",
        label: "Invoices",
        icon: renderIcon(Invoice01Icon),
        featureName: "Invoices",
      },
      {
        key: "quotations",
        label: "Quotations",
        icon: renderIcon(Calculator01Icon),
        featureName: "Quotations",
      },
      {
        key: "proposals",
        label: "Proposals",
        icon: renderIcon(NoteEditIcon),
        featureName: "Proposals",
      },
    ],
  },
  {
    type: "group",
    label: "Lead Views",
    children: [
      {
        key: "leads",
        label: "My Assigned Leads",
        icon: renderIcon(Contact01Icon),
        featureName: "My Assigned Leads",
      },
      {
        key: "pipeline",
        label: "Pipeline",
        icon: renderIcon(ChartHistogramIcon),
        featureName: "Pipeline",
      },
      {
        key: "interested",
        label: "Hot / Qualified Leads",
        icon: renderIcon(SmileIcon),
        featureName: "Hot / Qualified Leads",
      },
      {
        key: "dnp",
        label: "Unreachable / Callbacks",
        icon: renderIcon(Call02Icon),
        featureName: "Unreachable / Callbacks",
      },
      {
        key: "converted",
        label: "Converted",
        icon: renderIcon(ChampionIcon),
        featureName: "Converted",
      },
      {
        key: "favourite",
        label: "Priority Leads",
        icon: renderIcon(StarIcon),
        featureName: "Priority Leads",
      },
    ],
  },
  {
    type: "group",
    label: "Execution",
    children: [
      {
        key: "activities",
        label: "Schedule",
        icon: renderIcon(Calendar01Icon),
        featureName: "Schedule",
      },
      {
        key: "today-calls",
        label: "Today’s Call Log",
        icon: renderIcon(Time02Icon),
        featureName: "Today’s Call Log",
      },
      {
        key: "crm_client_onboard_requests",
        label: "Onboarding",
        icon: renderIcon(FileValidationIcon),
        featureName: "Onboarding",
        weCrmOnly: true,
      },
    ],
  },
];

/**
 * Filter and format menu items based on role, CRM flags, and search keyword
 */
export function buildMenuItems({ role = "admin", isCrmAdmin = false, searchTerm = "", companyCode = "", weCrmAccess = false }) {
  let baseConfig = ADMIN_MENU_CONFIG;
  if (role === "super_admin") {
    baseConfig = SUPER_ADMIN_MENU_CONFIG;
  } else if (role !== "admin") {
    baseConfig = EMPLOYEE_MENU_CONFIG;
  }
  const term = searchTerm.trim().toLowerCase();
  const targetWeCrmAccess = (process.env.REACT_APP_WE_CRM_ACCESS || WE_CRM_ACCESS || "").trim().toUpperCase();
  const hasWeCrmAccess = weCrmAccess || (Boolean(targetWeCrmAccess) && Boolean(companyCode) && String(companyCode).trim().toUpperCase() === targetWeCrmAccess);

  const filterItem = (item) => {
    // If CRM-only folder and user is not CRM admin, skip
    if (item.crmOnly && !isCrmAdmin) {
      return null;
    }

    // If WE_CRM only item and company does not have WE_CRM_ACCESS, skip
    if (item.weCrmOnly && !hasWeCrmAccess) {
      return null;
    }

    // Group or SubMenu with children
    if (item.children) {
      const filteredChildren = item.children.map(filterItem).filter(Boolean);
      if (filteredChildren.length === 0) return null;
      return {
        ...item,
        children: filteredChildren,
      };
    }

    // Regular single leaf item
    if (!term) return item;

    const matches = (item.label && String(item.label).toLowerCase().includes(term)) || (item.featureName && String(item.featureName).toLowerCase().includes(term));

    return matches ? item : null;
  };

  return baseConfig.map(filterItem).filter(Boolean);
}
